import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { formatDate, formatViews, getRelatedVideos, getVideoById } from '@/lib/videoData';
import { getCategoryMeta } from '@/types/video';
import { useSeo } from '@/hooks/useSeo';

export default function Detail() {
  const { id = '' } = useParams();
  const video = getVideoById(id);
  const meta = video ? getCategoryMeta(video.category) : undefined;
  const related = video ? getRelatedVideos(video, 8) : [];

  const [toast, setToast] = useState('');
  const [copied, setCopied] = useState(false);

  useSeo({
    title: video ? `${video.title} - 桃子视频` : '视频不存在 - 桃子视频',
    description: video
      ? video.description.slice(0, 100)
      : '抱歉，你访问的视频不存在或已下架，请返回首页浏览其他内容。',
    keywords: video
      ? `${video.title},桃子视频,${meta?.name},高清视频`
      : '桃子视频',
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // 剪贴板不可用时仍给出提示
    }
    setCopied(true);
    setToast('链接已复制到剪贴板（前端演示）');
    setTimeout(() => setCopied(false), 2200);
  };

  if (!video) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="mx-auto flex max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-8">
          <i className="ri-file-unknow-line text-5xl text-foreground-600" />
          <h1 className="mt-4 text-xl font-semibold text-foreground-50">视频不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-400">
            你访问的视频可能已被移除，去看看其他热门内容吧。
          </p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const metaItems = [
    { icon: 'ri-price-tag-3-line', label: '类型', value: meta?.name ?? '' },
    { icon: 'ri-earth-line', label: '地区', value: video.region },
    { icon: 'ri-calendar-line', label: '上映年份', value: String(video.year) },
    { icon: 'ri-refresh-line', label: '更新时间', value: formatDate(video.updateDate) },
    { icon: 'ri-star-line', label: '评分', value: video.rating.toFixed(1) },
    { icon: 'ri-fire-line', label: '热度', value: `${formatViews(video.views)}次播放` },
  ];

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: meta?.name ?? '分类', to: `/category/${video.category}` },
            { label: video.title },
          ]}
        />

        <section className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          <div className="relative overflow-hidden rounded-lg border border-background-800 bg-background-900">
            <div className="aspect-[3/4] w-full">
              <img
                src={video.cover}
                alt={`${video.title}封面`}
                title={`${video.title} - 桃子视频`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-background-950/30 opacity-0 transition-opacity hover:opacity-100">
              <button
                type="button"
                onClick={() => setToast('本站为内容演示站点，暂不支持在线播放')}
                className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105"
                aria-label="播放预览"
              >
                <i className="ri-play-fill text-3xl" />
              </button>
            </div>
          </div>

          <div className="flex flex-col">
            <h1 className="font-heading text-2xl font-bold leading-tight text-foreground-50 md:text-4xl">
              {video.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-500/20 px-3 py-1 text-xs font-medium text-secondary-300"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              {metaItems.map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-sm">
                  <i className={`${item.icon} text-foreground-500`} />
                  <span className="text-foreground-500">{item.label}</span>
                  <span className="text-foreground-100">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-800/80 bg-background-900/60 p-4 md:p-5">
              <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-foreground-50">
                <i className="ri-file-text-line text-primary-400" />
                视频简介
              </h2>
              <p className="text-sm leading-relaxed text-foreground-300">{video.description}</p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setToast('本站为内容演示站点，暂不支持在线播放')}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg" />
                播放视频
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-5 py-3 text-sm font-medium text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                <i className={copied ? 'ri-check-line' : 'ri-share-forward-line'} />
                {copied ? '已复制' : '分享'}
              </button>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground-50 md:text-xl">
              <i className="ri-movie-line text-primary-400" />
              相关推荐
            </h2>
            <Link
              to={`/category/${video.category}`}
              className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-300 transition-colors hover:text-primary-400"
            >
              更多{meta?.name}
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {related.map((item) => (
              <VideoCard key={item.id} video={item} />
            ))}
          </div>
        </section>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 animate-pop-in">
          <div className="flex items-center gap-2 rounded-lg border border-background-700 bg-background-800 px-5 py-3 text-sm text-foreground-50 shadow-lg">
            <i className="ri-information-line text-primary-400" />
            {toast}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}